import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { NAV_ITEMS, scrollToId } from '../lib/scroll';
import useActiveSection from '../hooks/useActiveSection';
import logo from '../assets/img/logo.svg';

const NAV_IDS = NAV_ITEMS.map((i) => i.id);

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(NAV_IDS);
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.85);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (headerRef.current) headerRef.current.inert = !scrolled;
  }, [scrolled]);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header
      ref={headerRef}
      className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ${
        scrolled ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
      }`}
    >
      <div className="bg-ink-900/80 backdrop-blur border-b border-hairline">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 lg:px-12 h-16">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top">
            <img src={logo} alt="Gravity Technologies" className="h-6 w-auto" />
          </button>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-8 font-gotham">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                aria-current={active === item.id ? 'true' : undefined}
                className={`text-sm transition-colors duration-300 hover:text-brand-yellow ${
                  active === item.id ? 'text-brand-yellow' : 'text-brand-light-gray'
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => go('contact')}
              className="bg-brand-yellow text-ink-900 font-bold text-sm px-5 py-2 hover:bg-brand-yellow-dark transition-colors"
            >
              Start a project
            </button>
          </nav>

          <button className="md:hidden text-white" onClick={() => setOpen(true)} aria-label="Open menu">
            <Bars3Icon className="h-7 w-7" />
          </button>
        </div>
      </div>

      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-[70]">
          <Transition.Child as={Fragment} enter="duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/80" />
          </Transition.Child>
          <Transition.Child as={Fragment} enter="duration-300" enterFrom="translate-x-full" enterTo="translate-x-0" leave="duration-200" leaveFrom="translate-x-0" leaveTo="translate-x-full">
            <Dialog.Panel className="fixed inset-y-0 right-0 w-72 bg-ink-850 border-l border-hairline p-8">
              <Dialog.Title className="sr-only">Navigation</Dialog.Title>
              <div className="flex justify-end">
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-white">
                  <XMarkIcon className="h-7 w-7" />
                </button>
              </div>
              <nav className="mt-10 flex flex-col gap-8 font-gotham text-2xl">
                {NAV_ITEMS.map((item) => (
                  <button key={item.id} onClick={() => go(item.id)} className="text-left text-white hover:text-brand-yellow transition-colors">
                    {item.label}
                  </button>
                ))}
              </nav>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </header>
  );
}
